class TSMainSearch extends HTMLElement {
  constructor() {
    super();
    this.searchInput = this.querySelector("#MainSearch");
    this.loadMoreButton = this.querySelector('[data-trigger="load-more"]');
    this.isLoading = false;
    this.sortBy = this.dataset.sort;
    this.pagesSize = parseInt(this.dataset.pages);
    this.currentPage = parseInt(this.dataset.current);
    this.productsToShow = 16;

    this.grid = this.querySelector("#TSSearchGrid");

    this.optionPopup = this.querySelector("[data-united-controls]");
    this.filters = this.querySelector("#TSMainFilters");
    this.MOBILE_BREAKPOINT = 990;

    this.blocksShowed = 1;
    this.oneBlockMax = 16;
    this.amountOfProducts = parseInt(this.dataset.amount);

    this.autoScrollEnabled = false;
    this.io = null;

    this.initObserver();

    this.updateProductsToDisplay();
    this.initClickEvents();
    this.initChangeEvents();
    this.initInputEvent();

    this.initResizeEvents();
  }

  initResizeEvents() {
    window.addEventListener(
      "resize",
      debounce(() => {
        if (
          this.filters.classList.contains("open") ||
          this.optionPopup.classList.contains("open")
        )
          document.documentElement.style.overflow = "hidden";

        if (window.innerWidth >= this.MOBILE_BREAKPOINT)
          document.documentElement.style.overflow = "auto";
      }, 150)
    );
  }
  initObserver() {
    if (!this.loadMoreButton) return;

    this.io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || !entry.isIntersecting) return;
        if (!this.autoScrollEnabled)
          return console.log("Auto-scroll is disabled");
        if (this.isLoading) return console.log("Currently loading, wait...");
        if (this.isExhausted()) return console.log("No more products to load");

        // Trigger the next page when the button scrolls into view
        this.loadMoreProducts();
      },
      {
        root: null,
        rootMargin: "300px 0px", // pre-fetch a bit before bottom
        threshold: 0,
      }
    );

    // We don't observe yet; only after first click (per requirement).
  }
  enableAutoScroll() {
    this.autoScrollEnabled = true;
    this.loadMoreButton.classList.remove("hidden");
    if (this.io && this.loadMoreButton) {
      // console.log("Enabling auto-scroll");
      this.io.observe(this.loadMoreButton);
    }
  }
  disableAutoScroll() {
    console.log("Disabling auto-scroll");
    this.autoScrollEnabled = false;
    if (this.io && this.loadMoreButton) this.io.unobserve(this.loadMoreButton);
    document
      .querySelector(`[data-trigger="load-more"]`)
      .classList.add("hidden");
  }
  isExhausted() {
    console.log("Checking if exhausted", this.currentPage >= this.pagesSize);
    return this.currentPage >= this.pagesSize;
  }

  async searchChange(query) {
    // console.log("Search query changed:", query);

    if (this.filters) {
      this.querySelectorAll('[data-trigger="filter-toggle"]').forEach(
        (toggle) => {
          toggle.classList.remove("open");
        }
      );
      this.filters.classList.remove("open");
    }

    const trimmedQuery = query.trim();
    const encodedQuery = encodeURIComponent(trimmedQuery);
    const searchParams = new URLSearchParams();
    searchParams.append("q", trimmedQuery);
    searchParams.append("type", "product");

    const newProductUrl =
      window.location.pathname + `?${searchParams.toString()}`;
    window.history.replaceState({}, "", newProductUrl);

    // Suggestions
    try {
      const suggestionsResponse = await fetch(
        window.routes.predictive_search_url +
          `/?q=${trimmedQuery}&section_id=ts-predictive-results&type=product`
      );

      const suggestionsHtml = await suggestionsResponse.text();
      const suggestionsMarkup = new DOMParser().parseFromString(
        suggestionsHtml,
        "text/html"
      );

      const searchResultsQuery = suggestionsMarkup.querySelector(
        ".ts-search__results-queries"
      );

      if (searchResultsQuery)
        this.querySelector("[data-predictions]").innerHTML =
          searchResultsQuery?.innerHTML;
    } catch (error) {
      console.error("Error while fetch suggestions:", error);
    }

    try {
      const response = await fetch(
        window.routes.search_url + `/?q=${trimmedQuery}&type=product`
      );
      const html = await response.text();
      const responseMarkup = new DOMParser().parseFromString(html, "text/html");

      this.querySelector("[data-content]").innerHTML =
        responseMarkup.querySelector("ts-main-search [data-content]").innerHTML;

      this.filters = this.querySelector("#TSMainFilters");
      this.optionPopup = this.querySelector("[data-united-controls]");

      const newSectionData = responseMarkup.querySelector("ts-main-search");

      this.setAttribute("data-current", newSectionData?.dataset?.current || 1);
      this.setAttribute("data-pages", newSectionData?.dataset?.pages || 1);
      this.setAttribute("data-amount", newSectionData?.dataset?.amount || 0);
      this.enableAutoScroll();
      this.updateProductsToDisplay();

      if (wishlist && wishlist.resetProducts) {
        wishlist.resetProducts();
      }
    } catch (error) {
      //console.error("Error while fetch search:", error);
    }

    // reset page
    this.updateInfoFromAttributes();
  }

  filtersUpdate(exclusion = false) {
    this.toggleLoading();

    const oldSearchParams = new URLSearchParams(window.location.search);
    const searchParams = new URLSearchParams();

    this.setAttribute("data-current", 1);
    this.updateInfoFromAttributes();

    if (exclusion !== "*") {
      this.filters.querySelectorAll("input:checked").forEach((input) => {
        if (exclusion !== input.name) {
          searchParams.append(input.name, input.value);
        }
      });

      this.filters.querySelectorAll("[data-prices] input").forEach((input) => {
        if (exclusion !== input.dataset.param) {
          searchParams.append(input.name, input.value);
        }
      });
    }

    searchParams.append("page", this.currentPage);
    searchParams.append("sort_by", this.sortBy);
    searchParams.append("q", oldSearchParams.get("q"));

    const newProductUrl =
      window.location.pathname + `?${searchParams.toString()}`;
    window.history.replaceState({}, "", newProductUrl);

    fetch(newProductUrl)
      .then((res) => res.text())
      .then((body) => {
        const html = new DOMParser().parseFromString(body, "text/html");
        const section = html.querySelector("ts-main-search");
        const newFilters = html.querySelector("#TSMainFilters");
        const optionsPopup = html.querySelector("[data-united-controls]");

        this.querySelector("#TSSearchGrid").innerHTML =
          html.querySelector("#TSSearchGrid").innerHTML;

        this.grid.innerHTML = html.querySelector("#TSSearchGrid").innerHTML;

        this.optionPopup.innerHTML = optionsPopup.innerHTML;

        this.setAttribute("data-pages", section.dataset.pages);
        this.setAttribute("data-amount", section.dataset.amount);

        if (this.querySelector("[data-results-count]"))
          this.querySelector("[data-results-count]").innerText =
            section.dataset.amount;
        this.updateInfoFromAttributes();

        this.blocksShowed = 1;
        this.updateProductsToDisplay();

        newFilters.querySelectorAll("[data-index]").forEach((newTab) => {
          const currentTab = this.filters.querySelector(
            `[data-index="${newTab.dataset.index}"]`
          );

          currentTab.classList.toggle(
            "has-values",
            newTab.classList.contains("has-values")
          );
          currentTab.querySelector("[data-count]").innerText =
            newTab.querySelector("[data-count]").innerText;

          if (currentTab.querySelector("[data-values]")) {
            currentTab.querySelector("[data-values]").innerHTML =
              newTab.querySelector("[data-values]").innerHTML;
          }

          if (currentTab.classList.contains("open")) {
            currentTab.style.maxHeight = currentTab.scrollHeight + "px";
          }
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.toggleLoading();
      });
  }

  updateProductsToDisplay() {
    if (!this.querySelector("#TSSearchGrid")) return;

    const amountProductCanBeShowed = this.productsToShow * this.blocksShowed;

    this.querySelector("#TSSearchGrid")
      .querySelectorAll(".ts-collection__product")
      .forEach((product, index) => {
        product.classList.toggle("hide", index >= amountProductCanBeShowed);
      });

    //this.querySelector("[data-display-count]").innerText = this.productsToShow;

    this.loadMoreButton.classList.toggle(
      "hidden",
      amountProductCanBeShowed >= this.amountOfProducts
    );
  }

  loadMoreProducts() {
    // console.log("Load more products");
    // console.log("Current page:", this.currentPage);
    // console.log("Page size:", this.pagesSize);

    if (this.currentPage > this.pagesSize)
      return console.log("No more pages to load");
    this.enableAutoScroll();
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("page", this.currentPage + 1);

    this.toggleLoading();

    fetch(
      window.location.pathname + "?" + searchParams.toString() + `&type=product`
    )
      .then((response) => response.text())
      .then((body) => {
        const html = new DOMParser().parseFromString(body, "text/html");
        const gridWithNewProducts = document.getElementById("TSSearchGrid");
        const productsList = html.querySelectorAll(
          "#TSSearchGrid .ts-collection__product"
        );

        productsList.forEach((product) => {
          product.classList.add("hide");
          gridWithNewProducts.append(product);
        });

        // Update attributes
        this.setAttribute(
          "data-current",
          html.querySelector("ts-main-search").dataset.current
        );
        this.updateInfoFromAttributes();

        this.blocksShowed += 1;
        this.updateProductsToDisplay();
        if (this.isExhausted()) this.disableAutoScroll();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.toggleLoading();
      });
  }
  openFilter(status) {
    this.filters.classList.toggle("open", status);
    this.toggleBodyScroll();
    console.log("Filter open status:", status);
  }
  closeFilter() {
    this.querySelectorAll('[data-trigger="filter-toggle"]').forEach(
      (toggle) => {
        toggle.classList.remove("open");
      }
    );
    this.filters.classList.remove("open");
    this.toggleBodyScroll();
  }

  toggleBodyScroll() {
    if (window.innerWidth > this.MOBILE_BREAKPOINT) return;
    document.documentElement.style.overflow =
      document.documentElement.style.overflow === "hidden" ? "auto" : "hidden";
  }
  initClickEvents() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-trigger")) {
        const filterTab = e.target.closest("[data-index]");
        const toggleTab = e.target.closest(".ts-collection__tab");
        switch (e.target.dataset.trigger) {
          case "filter-toggle":
            e.target.classList.toggle("open");
            this.openFilter(e.target.classList.contains("open"));
            break;
          case "filter-tab":
            filterTab.classList.toggle("open");
            // e.target.nextElementSibling.style.maxHeight =
            //   filterTab.classList.contains("open")
            //     ? e.target.nextElementSibling.scrollHeight + "px"
            //     : null;
            break;
          case "filter-reset":
            if (!this.isLoading) {
              this.filtersUpdate(filterTab.dataset.param);
            }
            break;
          case "load-more":
            if (!this.isLoading) this.loadMoreProducts();

            break;
          case "sort-toggle":
            toggleTab.classList.toggle("open");
            break;
          // case "display-toggle":
          //   toggleTab.classList.toggle("open");
          //   break;
          case "sort-select":
            if (!this.isLoading) {
              this.sortBy = e.target.dataset.value;
              this.setAttribute("data-sort", e.target.dataset.value);
              this.querySelector("[data-sort-label]").innerText =
                e.target.innerText;
              toggleTab.classList.remove("open");
              this.filtersUpdate();
            }
            break;
          // case "display-select":
          //   this.productsToShow = parseInt(e.target.dataset.value);
          //   this.blocksShowed = 1;
          //   this.querySelector("[data-display-label]").innerText =
          //     this.productsToShow;
          //   toggleTab.classList.remove("open");
          //   this.setAttribute("data-current", 1);
          //   this.updateInfoFromAttributes();
          //   this.querySelectorAll("#TSSearchGrid > li").forEach(
          //     (product, index) => {
          //       if (index >= this.oneBlockMax) {
          //         product.remove();
          //       }
          //     }
          //   );
          //   this.updateProductsToDisplay();
          //   break;
          case "filter-clear":
            if (!this.isLoading) {
              this.filtersUpdate("*");
            }
            break;
          case "filter-close":
            this.closeFilter();
            break;
          case "options-open":
            this.optionPopup.classList.add("open");
            this.toggleBodyScroll();
            break;
          case "options-close":
            this.optionPopup.classList.remove("open");
            this.toggleBodyScroll();
            break;
          case "submit-options":
            if (!this.isLoading) {
              this.sortBy = this.querySelector(
                "[data-united-controls]"
              ).dataset.sort;
              this.setAttribute(
                "data-sort",
                this.querySelector("[data-united-controls]").dataset.sort
              );
              this.productsToShow = parseInt(
                this.querySelector("[data-united-controls]").dataset.display
              );
              this.blocksShowed = 1;
              this.setAttribute("data-current", 1);
              this.querySelectorAll("#TSSearchGrid > li").forEach(
                (product, index) => {
                  if (index >= this.oneBlockMax) product.remove();
                }
              );
              this.updateInfoFromAttributes();
              this.updateProductsToDisplay();
              this.querySelector("[data-united-controls]").classList.remove(
                "open"
              );
              this.filtersUpdate();
              this.toggleBodyScroll();
            }
            break;
          case "sort-preselect":
            this.querySelector("[data-united-controls]").setAttribute(
              "data-sort",
              e.target.dataset.value
            );
            this.querySelectorAll("[data-sort-label]").forEach((label) => {
              label.innerText = e.target.innerText;
            });
            this.querySelector("[data-united-controls]")
              .querySelector(
                '.button-secondary--active[data-trigger="sort-preselect"]'
              )
              .classList.remove("button-secondary--active");

            e.target.classList.add("button-secondary--active");
            break;
          case "display-preselect":
            this.querySelector("[data-united-controls]").setAttribute(
              "data-display",
              e.target.dataset.value
            );
            this.querySelectorAll("[data-display-label]").forEach((label) => {
              label.innerText = e.target.innerText;
            });
            this.querySelector("[data-united-controls]")
              .querySelector('[data-trigger="display-preselect"][selected]')
              .removeAttribute("selected");
            e.target.setAttribute("selected", true);
            break;
          case "reset-search":
            this.searchInput.value = "";
            this.searchChange("");
            break;
          case "select-predictive-query":
            window.location.href =
              "/search/?q=" + this.searchInput.value.trim() + "&type=product";
            break;
          case "select-query":
            this.searchInput.value = e.target.innerText.toLowerCase();
            this.searchChange(e.target.innerText.toLowerCase());
            break;
        }
      }
    });
  }

  initChangeEvents() {
    this.addEventListener("change", (e) => {
      if (e.target.closest("#TSMainFilters") && !this.isLoading) {
        this.filtersUpdate();
      }
    });
  }

  initInputEvent() {
    this.addEventListener("input", (e) => {
      if (e.target.closest(".ts-filters__values--range")) {
        this.updatePriceRange(e.target, e.target.type);
      }
    });
    this.searchInput.addEventListener(
      "input",
      debounce((e) => {
        this.searchChange(e.target.value);
      }, 500).bind(this)
    );
  }

  updatePriceRange(target, type = "range") {
    const rangeInputs = this.querySelectorAll("[data-ranges] input");
    const minVal = parseInt(rangeInputs[0].value);
    const maxVal = parseInt(rangeInputs[1].value);

    const priceInputs = this.querySelectorAll("[data-prices] input");
    const minPrice = parseInt(priceInputs[0].value);
    const maxPrice = parseInt(priceInputs[1].value);

    const rangeProgress = this.querySelector("[range-progress]");
    const priceGap = 10;

    if (type == "number") {
      if (maxPrice - minPrice >= priceGap && maxPrice <= rangeInputs[1].max) {
        if (target.className === "input-min") {
          rangeInputs[0].value = minPrice;
          rangeProgress.style.left =
            (minPrice / rangeInputs[0].max) * 100 + "%";
        } else {
          rangeInputs[1].value = maxPrice;
          rangeProgress.style.right =
            100 - (maxPrice / rangeInputs[1].max) * 100 + "%";
        }
      }
    } else {
      if (maxVal - minVal < priceGap) {
        if (target.hasAttribute("range-min")) {
          rangeInputs[0].value = maxVal - priceGap;
        } else {
          rangeInputs[1].value = minVal + priceGap;
        }
      } else {
        priceInputs[0].value = minVal;
        priceInputs[1].value = maxVal;
        rangeProgress.style.left = (minVal / rangeInputs[0].max) * 100 + "%";
        rangeProgress.style.right =
          100 - (maxVal / rangeInputs[1].max) * 100 + "%";
      }
    }
  }

  updateInfoFromAttributes() {
    this.pagesSize = parseInt(this.dataset.pages);
    this.currentPage = parseInt(this.dataset.current);
    this.amountOfProducts = parseInt(this.dataset.amount);
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

customElements.define("ts-main-search", TSMainSearch);
